import {DEFAULT_MAX_NUM_RUNS} from '../../features/workspaceSettings';
import {EditableWorkspaceSettings} from '../../features/workspaceSettings/store';
import {PanelSettings} from '../../state/views/panelSettings/types';
import {POINT_VISUALIZATION_OPTIONS} from '../WorkspaceSettingsModal/controls/PointVisualization';
import {PanelBankSettings} from './types';

type CustomWorkspaceUpdate = {
  key: keyof EditableWorkspaceSettings;
  // this is a condition where values that are undefined in the DB won't be written to the local views and defaulting is deferred to the view logic
  updateCondition: boolean;
  // a way to get the value for the update
  accessor: () => any;
  // in the event we get any kind of non-conforming value we can provide a safe default before piping it into the view logic
  dataDefaultFn?: (val: any) => any;
};

export const generateUpdates = (
  panelBankSettings: PanelBankSettings,
  panelSettings: PanelSettings,
  isCustomWorkspace: boolean,
  hasGorillaRunsAccess: boolean
): CustomWorkspaceUpdate[] => [
  {
    key: 'autoOrganizePrefix',
    updateCondition: panelBankSettings.autoOrganizePrefix != null,
    accessor: () => panelBankSettings.autoOrganizePrefix,
  },
  {
    key: 'colorRunNames',
    updateCondition: panelSettings.colorRunNames != null,
    accessor: () => panelSettings.colorRunNames,
  },
  {
    key: 'highlightedCompanionRunOnly',
    updateCondition:
      typeof panelBankSettings.highlightedCompanionRunOnly === 'boolean',
    accessor: () => panelBankSettings.highlightedCompanionRunOnly,
  },
  {
    key: 'maxRuns',
    updateCondition: !!panelSettings.maxRuns,
    accessor: () => panelSettings.maxRuns,
    dataDefaultFn: (val: number) => {
      if (val > DEFAULT_MAX_NUM_RUNS) {
        return DEFAULT_MAX_NUM_RUNS;
      }
      return val;
    },
  },
  {
    key: 'pointVisualizationMethod',
    // if the user doesn't have weave runs access, don't allow an update
    // this will keep a user from choosing bucketing, flipping a toggle, and having the
    // setting continue to be active because it's saved in the spec
    updateCondition:
      (panelSettings.pointVisualizationMethod != null &&
        !Object.values(POINT_VISUALIZATION_OPTIONS).includes(
          panelSettings.pointVisualizationMethod
        )) ||
      (panelSettings.pointVisualizationMethod != null &&
        hasGorillaRunsAccess &&
        isCustomWorkspace),
    accessor: () => panelSettings.pointVisualizationMethod,
    dataDefaultFn: (val: string) => {
      // prevent any values outside the enum from being written to the store
      // safety check for values created during development that are no longer supported
      // @ts-expect-error
      if (!Object.values(POINT_VISUALIZATION_OPTIONS).includes(val)) {
        return POINT_VISUALIZATION_OPTIONS.Sampling;
      }
      return val;
    },
  },
  {
    key: 'showEmptySections',
    updateCondition: panelBankSettings.showEmptySections != null,
    accessor: () => panelBankSettings.showEmptySections,
  },
  {
    key: 'sortAlphabetically',
    updateCondition: panelBankSettings.sortAlphabetically != null,
    accessor: () => panelBankSettings.sortAlphabetically,
  },
  {
    key: 'suppressLegends',
    updateCondition: panelSettings.suppressLegends != null,
    accessor: () => panelSettings.suppressLegends,
  },
  {
    key: 'tooltipNumberOfRuns',
    updateCondition: panelSettings.tooltipNumberOfRuns != null,
    accessor: () => panelSettings.tooltipNumberOfRuns,
  },
];

export function customWorkspaceUpdate(
  updates: ReturnType<typeof generateUpdates>,
  isCustomWorkspace: boolean
) {
  const hasCustomWorkspaceSetting = updates.some(
    u => u.updateCondition === true
  );
  const updatesToApply = updates.reduce(
    (acc, {key, updateCondition, accessor, dataDefaultFn}) => {
      if (updateCondition) {
        const val = accessor();
        const safeVal = dataDefaultFn ? dataDefaultFn(val) : val;

        // @ts-ignore I don't know how to type this
        acc[key] = safeVal;
      }
      return acc;
    },
    {
      // default the update object to include the override if a custom workspace feature is used
      // this means any workspace mutated with a custom workspace becomes a custom workspace forever
      isCustomWorkspace: isCustomWorkspace || hasCustomWorkspaceSetting,
    } as Partial<PanelBankSettings>
  );

  return updatesToApply;
}
